import 'client-only';

import * as Sentry from '@sentry/nextjs';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { BRANDS, type Brand } from '@/consts/brands';
import { type AnalyticsPatientResource } from '@/types/metrics/analyticsPatient';

declare global {
  interface Window {
    CUROLOGY_ENV?: {
      APP_BRAND?: string;
    };
    duet?: {
      setMetadata: (metadata: {
        email?: string;
        name?: string;
        path?: string;
      }) => void;
      setUserId: (userId: string) => void;
      setUserIdAuth: (signature: string, epoch: number) => void;
      updateStyle: (style: { [key: string]: string | number }) => void;
    };
  }
}

interface SignatureToken {
  user_id: string;
  signature: string;
  epoch: number;
}

const BRAND_APP_URLS: Record<Brand, string | undefined> = {
  [BRANDS.curology]: process.env.NEXT_PUBLIC_CUROLOGY_APP_URL,
  [BRANDS.agency]: process.env.NEXT_PUBLIC_AGENCY_APP_URL,
};

// Carries the HTTP status so Sentry gets a legible title/grouping (not a raw Response).
class SignatureTokenHttpError extends Error {
  constructor(public readonly status: number) {
    super(`Decagon signature token fetch failed: ${status}`);
    this.name = 'SignatureTokenHttpError';
  }
}

// Set on pagehide (unload/tab-close/bfcache); cleared on pageshow so a bfcache restore doesn't silence a teardown-cancelled error forever.
let pageIsUnloading = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    pageIsUnloading = true;
  });
  window.addEventListener('pageshow', () => {
    pageIsUnloading = false;
  });
}

// Silence only teardown/offline; anything else status-less (CORS/DNS/TLS) reports.
const isSilencedFetchError = (): boolean =>
  pageIsUnloading ||
  (typeof navigator !== 'undefined' && navigator.onLine === false);

const fetchSignatureToken = (brand: Brand): Promise<SignatureToken | null> => {
  const baseUrl = BRAND_APP_URLS[brand];
  if (!baseUrl) return Promise.resolve(null);
  return fetch(`${baseUrl}/api/v1/decagon/auth/signature-token`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then((res) =>
      res.ok
        ? (res.json() as Promise<SignatureToken>)
        : Promise.reject(new SignatureTokenHttpError(res.status)),
    )
    .catch((error: unknown): null => {
      if (error instanceof SignatureTokenHttpError) {
        // Report everything but an expected 401 (anonymous/expired).
        if (error.status !== 401) {
          Sentry.captureException(error, {
            tags: { context: 'decagon_signature_token' },
          });
        }
        return null;
      }
      // Status-less: silence teardown/offline, report the rest (CORS/DNS/TLS).
      if (!isSilencedFetchError()) {
        Sentry.captureException(error, {
          tags: { context: 'decagon_signature_token' },
        });
      }
      return null;
    });
};

const decagonIframeLoaded = (
  brand: string | null | undefined,
  callback: () => void,
) => {
  if (typeof document === 'undefined' || !brand) {
    return () => undefined;
  }

  const MAX_ATTEMPTS = 60;
  const BASE_DELAY_MS = 50;
  const MAX_DELAY_MS = 2000;

  let timeoutId: number | undefined;
  let iframe: Element | null = null;
  let attempts = 0;
  const handler = () => callback();

  const tryAttachListener = () => {
    attempts += 1;
    iframe = document.querySelector(`[src="https://decagon.ai/demo/${brand}"]`);

    if (iframe instanceof HTMLIFrameElement) {
      if (isIframeLoaded(iframe)) {
        callback();
      } else {
        iframe.addEventListener('load', handler, { once: true });
      }
      return;
    }

    if (attempts >= MAX_ATTEMPTS) {
      console.warn(
        '[useDecagonChatBot] Decagon iframe not found after polling; giving up.',
      );
      return;
    }

    const delay = Math.min(
      BASE_DELAY_MS * 2 ** Math.max(attempts - 1, 0),
      MAX_DELAY_MS,
    );
    timeoutId = window.setTimeout(tryAttachListener, delay);
  };

  tryAttachListener();

  return () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
    if (iframe instanceof HTMLIFrameElement) {
      iframe.removeEventListener('load', handler);
    }
  };
};

const scheduleWhenIdle = (
  win: Window & typeof globalThis,
  callback: () => void,
  timeout: number,
) => {
  if (typeof win.requestIdleCallback === 'function') {
    const idleHandle = win.requestIdleCallback(
      () => {
        callback();
      },
      { timeout },
    );

    return () => {
      if (typeof win.cancelIdleCallback === 'function') {
        win.cancelIdleCallback(idleHandle);
      }
    };
  }

  const timeoutHandle = win.setTimeout(callback, 0);

  return () => {
    win.clearTimeout(timeoutHandle);
  };
};

const isIframeLoaded = (iframe: HTMLIFrameElement) => {
  try {
    return iframe.contentWindow?.document?.readyState === 'complete';
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.debug(
        '[useDecagonChatBot] Unable to inspect Decagon iframe state.',
        error,
      );
    }
    return false;
  }
};

const DECAGON_SCRIPT_ID = 'decagon-script';

const hasDecagonScript = () =>
  Boolean(document.getElementById(DECAGON_SCRIPT_ID));

const appendDecagonScript = (brandName: string, onLoad: () => void) => {
  const script = document.createElement('script');
  script.setAttribute('id', DECAGON_SCRIPT_ID);
  script.src = `https://decagon.ai/loaders/${brandName}.js`;
  script.async = true;
  script.addEventListener('load', onLoad, { once: true });
  script.addEventListener(
    'error',
    (error) => {
      console.error(
        '[useDecagonChatBot] Failed to load Decagon chatbot script.',
        error,
      );
    },
    { once: true },
  );
  document.body.appendChild(script);
};

const applySignatureAuth = (
  win: Window & typeof globalThis,
  token: SignatureToken | null,
  hasValidAuth: { current: boolean },
  isCancelled: () => boolean,
) => {
  if (!token || isCancelled() || !win.duet || hasValidAuth.current) return;
  hasValidAuth.current = true;
  win.duet.setUserId(token.user_id);
  win.duet.setUserIdAuth(token.signature, token.epoch);
};

// Rebuild the widget so the authed identity owns a fresh conversation (Decagon 404s the anon one in place; continuity needs /conversation/link — follow-up). Best-effort teardown (removing <script> can't unload the executed loader); returns the new iframe-poll disposer for cleanup.
const reinitializeDecagonWidget = (
  brandName: string,
  onLoad: () => void,
): (() => void) => {
  document.getElementById(DECAGON_SCRIPT_ID)?.remove();
  document
    .querySelectorAll('iframe[src^="https://decagon.ai/"]')
    .forEach((node) => {
      node.remove();
    });
  delete (window as Window & typeof globalThis).duet;
  const disposeIframePoll = decagonIframeLoaded(brandName, onLoad);
  appendDecagonScript(brandName, onLoad);
  return disposeIframePoll;
};

const updateDuetMetadata = (
  win: Window & typeof globalThis,
  patient?: AnalyticsPatientResource,
) => {
  win.duet?.setMetadata({
    email: patient?.email,
    name: patient?.fullName,
    path: win.location.pathname,
  });

  win.duet?.updateStyle({
    zIndex: 999994,
  });
};

interface ApplyMetadataDeps {
  win: Window & typeof globalThis;
  brand?: Brand;
  brandName: string;
  patient?: AnalyticsPatientResource;
  hasValidAuth: { current: boolean };
  tokenPromise: { current: Promise<SignatureToken | null> | null };
  resolvedSignatureToken: { current: SignatureToken | null | undefined };
  // A patient just authed onto a live anonymous widget — rebuild it (Decagon 404s the anon one in place).
  reinitForAuth: boolean;
  isCancelled: () => boolean;
  // Registers the rebuild's iframe-poll disposer so the effect cleanup can cancel it.
  registerIframeCleanup: (dispose: () => void) => void;
}

// Builds the callback that syncs metadata and applies (or rebuilds for) the signed identity.
const buildApplyMetadata = (deps: ApplyMetadataDeps) => {
  const {
    win,
    brand,
    brandName,
    patient,
    hasValidAuth,
    tokenPromise,
    resolvedSignatureToken,
    reinitForAuth,
    isCancelled,
    registerIframeCleanup,
  } = deps;
  let reinitialized = false;

  const applyOrReinit = (token: SignatureToken | null) => {
    if (
      !brand ||
      hasValidAuth.current ||
      !win.duet ||
      !token ||
      isCancelled()
    ) {
      return;
    }
    if (reinitForAuth && !reinitialized) {
      reinitialized = true;
      registerIframeCleanup(
        reinitializeDecagonWidget(brandName, applyMetadata),
      );
      return;
    }
    applySignatureAuth(win, token, hasValidAuth, isCancelled);
  };

  function applyMetadata() {
    if (isCancelled()) return;
    updateDuetMetadata(win, patient);
    applyOrReinit(resolvedSignatureToken.current ?? null);

    if (brand && !hasValidAuth.current && tokenPromise.current === null) {
      tokenPromise.current = fetchSignatureToken(brand);
      tokenPromise.current.then((token) => {
        resolvedSignatureToken.current = token;
        applyOrReinit(token);
      });
    }
  }

  return applyMetadata;
};

export const useDecagonChatBot = (
  brand?: Brand,
  patient?: AnalyticsPatientResource,
) => {
  const brandName = brand ?? window?.CUROLOGY_ENV?.APP_BRAND ?? patient?.brand;
  const pathname = usePathname();
  const tokenPromise = useRef<Promise<SignatureToken | null> | null>(null);
  /** Token from the latest fetch for this effect run; set when the promise settles. */
  const resolvedSignatureToken = useRef<SignatureToken | null | undefined>(
    undefined,
  );
  const hasValidAuth = useRef(false);

  useEffect(() => {
    if (!brandName) {
      return;
    }

    if (!hasValidAuth.current) {
      tokenPromise.current = null;
      resolvedSignatureToken.current = undefined;
    }

    const win = window as Window & typeof globalThis;
    // Patient on an already-live widget we haven't authed → it booted anonymous, so rebuild (keyed on hasValidAuth not a patient latch, so a 401-on-arrival run leaves the retry open, not applied in place).
    let cancelled = false;
    let cleanupIframeListener: (() => void) | undefined;

    const applyMetadata = buildApplyMetadata({
      win,
      brand,
      brandName,
      patient,
      hasValidAuth,
      tokenPromise,
      resolvedSignatureToken,
      reinitForAuth:
        Boolean(win.duet) && !hasValidAuth.current && Boolean(patient),
      isCancelled: () => cancelled,
      registerIframeCleanup: (dispose) => {
        cleanupIframeListener?.();
        cleanupIframeListener = dispose;
      },
    });

    const ensureScriptLoaded = () => {
      if (cancelled) {
        return;
      }

      cleanupIframeListener?.();
      cleanupIframeListener = decagonIframeLoaded(brandName, applyMetadata);

      if (hasDecagonScript()) {
        applyMetadata();
        return;
      }

      appendDecagonScript(brandName, applyMetadata);
    };

    if (win.duet) {
      applyMetadata();
      cleanupIframeListener = decagonIframeLoaded(brandName, applyMetadata);
      return () => {
        cancelled = true;
        cleanupIframeListener?.();
      };
    }

    const cleanupIdle = scheduleWhenIdle(win, ensureScriptLoaded, 4000);

    return () => {
      cancelled = true;
      cleanupIframeListener?.();
      cleanupIdle();
    };
  }, [brandName, patient, pathname]); // eslint-disable-line react-hooks/exhaustive-deps
};
